import { Controller } from "@hotwired/stimulus"
import { confirmChangeImpact } from "./support/change_impact"

// The single drag primitive of the app (docs/frontend.md). One controller
// for every surface where something is dragged — the leaves of a Document
// today, the Kanban of Cards tomorrow — because the old app grew one
// implementation per screen and they drifted apart.
//
// SortableJS arrives with the first drag, never in the main bundle
// (docs/carrega-llibreries-esporadiques.md). Without it the screen still
// reorders: the buttons on the list rows are the no-JS path.
export default class extends Controller {
  static values = {
    url: String,           // where a move is persisted, with :id to fill in
    method: { type: String, default: "post" },
    group: String,         // shared name when items travel between containers
    itemSelector: { type: String, default: "[data-sortable-id]" },
    sort: { type: Boolean, default: true },
    // Some grouped surfaces have a canonical order rather than a stored
    // position. A changed Card becomes the most recently active one, so its
    // optimistic DOM position is the start of the destination column.
    canonicalPosition: String,
    // No handle by default: the whole item is the grip, which is the target
    // a finger deserves. A surface that needs one declares its selector.
    handleSelector: String
  }

  async connect() {
    this.connected = true
    this.failed = false
    this.persistQueue = Promise.resolve()
    const connection = this.connection = Symbol("sortable-connection")

    const { default: Sortable } = await import("sortablejs")
    if (!this.connected || this.connection !== connection || !this.element.isConnected) return

    this.sortable = Sortable.create(this.element, {
      draggable: this.itemSelectorValue,
      handle: this.handleSelectorValue || undefined,
      // Buttons that live inside a draggable item (remove, up, down): a
      // press on them is a press, never the start of a drag.
      filter: "[data-sortable-filter]",
      preventOnFilter: false,
      group: this.groupValue || undefined,
      sort: this.sortValue,
      // The gap left behind is the answer to "where will this land": it
      // stays in the list, empty and outlined (docs/frontend.md).
      ghostClass: "sortable-gap",
      // What follows the finger
      fallbackClass: "sortable-lifted",
      // Its own pointer implementation everywhere: the native HTML5 drag
      // does not exist on touch — and Databitat is read on a phone — and it
      // fights the links the cards already are.
      forceFallback: true,
      // On touch, a press that is really a scroll must not become a drag
      delay: 120,
      delayOnTouchOnly: true,
      animation: 150,
      onEnd: (event) => this.enqueuePersist(event)
    })
  }

  disconnect() {
    this.connected = false
    this.connection = null
    this.sortable?.destroy()
    this.sortable = null
  }

  enqueuePersist({ item, newIndex, oldIndex, from, to }) {
    if (newIndex === oldIndex && from === to) return

    if (from !== to && this.canonicalPositionValue === "first") to.prepend(item)
    this.syncCounts(from, to)

    const move = {
      id: item.dataset.sortableId,
      item,
      position: newIndex,
      // For grouped lists, the destination owns the persistence URL. A
      // Kanban column can therefore encode its state in its own endpoint
      // without teaching this controller what a Card state is.
      url: to.dataset.sortableUrlValue || this.urlValue,
      method: to.dataset.sortableMethodValue || this.methodValue
    }

    // Preserve the order of rapid gestures. The optimistic DOM may move
    // again before the first request finishes, but the server sees the same
    // sequence the person performed.
    this.persistQueue = this.persistQueue.then(() => this.persist(move))
  }

  async persist({ id, item, position, url, method }, confirmationToken = null) {
    if (this.failed) return

    try {
      // The token is sent when the page carries one, and its absence is not
      // treated as a reason to refuse the request: CSRF is the server's call,
      // and a client that would not even ask cannot be exercised where
      // forgery protection is off — which is where system tests run, and is
      // how the drag went untested for so long (2026-08-18).
      const csrfToken = document.querySelector("meta[name=csrf-token]")?.content

      // Uppercased on purpose: fetch() normalizes the case of GET, POST, PUT,
      // DELETE, HEAD and OPTIONS but deliberately not of PATCH, so a lowercase
      // "patch" travels as-is and Puma rejects it at the HTTP parser with 400
      // ("Bad method patch") before Rails ever sees the request.
      const response = await fetch(url.replace(":id", id), {
        method: method.toUpperCase(),
        credentials: "same-origin",
        headers: {
          ...(csrfToken && { "X-CSRF-Token": csrfToken }),
          "Content-Type": "application/json",
          Accept: "application/json"
        },
        body: JSON.stringify({ position, confirmation_token: confirmationToken })
      })

      if ([409, 422].includes(response.status)) {
        const payload = await response.json()
        if (!payload.impact) throw new Error(`Move failed with ${response.status}`)

        const confirmed = await confirmChangeImpact(payload.impact)
        if (!confirmed || !payload.confirmation_token) {
          throw new Error("Move canceled after impact review")
        }

        return this.persist({ id, item, position, url, method }, payload.confirmation_token)
      }

      if (!response.ok) throw new Error(`Move failed with ${response.status}`)

      const payload = response.status === 204 ? null : await response.json()
      if (payload?.redirect_url) {
        window.Turbo.visit(payload.redirect_url)
        return
      }
      this.dispatch("persisted", { detail: { item, response: payload } })
    } catch (_error) {
      this.failed = true

      // The server is the order of record: a refused request or a network
      // failure restores its sequence instead of leaving a fictional move
      // on screen. If Turbo already disconnected us, the next page is the
      // fresh rendering and must not be reloaded.
      if (this.connected) window.location.reload()
    }
  }

  syncCounts(...lists) {
    new Set(lists).forEach((list) => {
      const column = list.closest("[data-sortable-column]")
      const count = column?.querySelector("[data-sortable-count]")
      if (count) count.textContent = list.children.length
    })
  }
}
